import type {
  SuspiciousTermSummary,
  SuspiciousWordCategory,
  SuspiciousWordHit,
} from "./types";

interface DictionaryEntry {
  id: string;
  term: string;
  label: string;
  category: SuspiciousWordCategory;
  risk: SuspiciousWordHit["risk"];
  reason: string;
  patterns: RegExp[];
}

export const SUSPICIOUS_WORD_DICTIONARY: DictionaryEntry[] = [
  {
    id: "access-token",
    term: "access token",
    label: "Access token",
    category: "credential",
    risk: "high",
    reason: "May identify live authentication material.",
    patterns: [/\baccess[_ .-]?token\b/i, /\bgetAccessToken\b/],
  },
  {
    id: "refresh-token",
    term: "refresh token",
    label: "Refresh token",
    category: "credential",
    risk: "high",
    reason: "May allow an attacker to mint new sessions.",
    patterns: [/\brefresh[_ .-]?token\b/i],
  },
  {
    id: "session-token",
    term: "session token",
    label: "Session token",
    category: "credential",
    risk: "high",
    reason: "May expose an authenticated session.",
    patterns: [/\bsession[_ .-]?token\b/i, /\bbearer\s+[a-z0-9._~-]+/i],
  },
  {
    id: "password",
    term: "password",
    label: "Password",
    category: "credential",
    risk: "high",
    reason: "References password material or password collection.",
    patterns: [/\bpasswords?\b/i, /\bpasswd\b/i, /\bpassphrase\b/i],
  },
  {
    id: "credential",
    term: "credential",
    label: "Credential",
    category: "credential",
    risk: "medium",
    reason: "References stored or collected credentials.",
    patterns: [/\bcredentials?\b/i, /\bLogin Data\b/i, /\bLocal State\b/i],
  },
  {
    id: "cookie",
    term: "cookie",
    label: "Browser cookie",
    category: "credential",
    risk: "medium",
    reason: "Browser cookies can contain authenticated sessions.",
    patterns: [/\bcookies?\b/i, /\bcookieStore\b/i],
  },
  {
    id: "dpapi",
    term: "dpapi",
    label: "DPAPI",
    category: "credential",
    risk: "high",
    reason: "Windows DPAPI is commonly used to decrypt locally stored secrets.",
    patterns: [/\bCryptUnprotectData\b/i, /\bDPAPI\b/i],
  },
  {
    id: "token",
    term: "token",
    label: "Token",
    category: "credential",
    risk: "context",
    reason: "Generic token wording requires surrounding behavior to establish risk.",
    patterns: [/\btoken\b/i, /\b[a-z]+Token\b/, /\btoken[A-Z][a-z]+\b/],
  },
  {
    id: "minecraft-username",
    term: "mcUsername",
    label: "Minecraft username",
    category: "identity",
    risk: "medium",
    reason: "Identifies collection of the active Minecraft account name.",
    patterns: [/\bmc[_ .-]?username\b/i, /\bgetUsername\b/],
  },
  {
    id: "minecraft-uuid",
    term: "mcUuid",
    label: "Minecraft UUID",
    category: "identity",
    risk: "medium",
    reason: "Identifies collection of the active Minecraft account UUID.",
    patterns: [/\bmc[_ .-]?uuid\b/i, /\bgetUuid(?:OrNull)?\b/],
  },
  {
    id: "uuid",
    term: "uuid",
    label: "UUID",
    category: "identity",
    risk: "context",
    reason: "UUIDs are common in legitimate mods and need behavioral context.",
    patterns: [/\buuid\b/i, /\bprofile[_ .-]?id\b/i],
  },
  {
    id: "username",
    term: "username",
    label: "Username",
    category: "identity",
    risk: "context",
    reason: "User identity access is contextual until paired with collection or exfiltration.",
    patterns: [/\buser[_ .-]?name\b/i, /\bprofileName\b/i],
  },
  {
    id: "stealer",
    term: "stealer",
    label: "Stealer",
    category: "stealer",
    risk: "high",
    reason: "Explicit stealer terminology is strongly suspicious.",
    patterns: [/\binfo[_ .-]?stealer\b/i, /\bstealer\b/i, /_stealer\.log\b/i],
  },
  {
    id: "grabber",
    term: "grabber",
    label: "Grabber",
    category: "stealer",
    risk: "high",
    reason: "Common self-description for credential and token theft modules.",
    patterns: [/\bgrabber\b/i, /\btoken[_ .-]?grabber\b/i],
  },
  {
    id: "token-logger",
    term: "token logger",
    label: "Token logger",
    category: "stealer",
    risk: "high",
    reason: "Describes collection or logging of authentication tokens.",
    patterns: [/\btoken[_ .-]?logger\b/i],
  },
  {
    id: "rat",
    term: "rat",
    label: "RAT",
    category: "stealer",
    risk: "high",
    reason: "May identify remote-access malware.",
    patterns: [/\bremote access trojan\b/i, /\bRAT\b/],
  },
  {
    id: "backdoor",
    term: "backdoor",
    label: "Backdoor",
    category: "stealer",
    risk: "high",
    reason: "Explicit backdoor terminology indicates hidden remote access.",
    patterns: [/\bbackdoor\b/i],
  },
  {
    id: "keylogger",
    term: "keylogger",
    label: "Keylogger",
    category: "surveillance",
    risk: "high",
    reason: "Identifies keyboard capture behavior.",
    patterns: [/\bkey[_ .-]?logger\b/i, /\bKeyEventDispatcher\b/],
  },
  {
    id: "clipper",
    term: "clipper",
    label: "Clipboard clipper",
    category: "surveillance",
    risk: "high",
    reason: "May identify clipboard theft or cryptocurrency address replacement.",
    patterns: [/\bclipper\b/i, /\bgetSystemClipboard\b/],
  },
  {
    id: "screenshot",
    term: "screenshot",
    label: "Screenshot",
    category: "surveillance",
    risk: "medium",
    reason: "Identifies screen capture capability.",
    patterns: [/\bscreen[_ .-]?shot\b/i, /\bcreateScreenCapture\b/],
  },
  {
    id: "webcam",
    term: "webcam",
    label: "Webcam",
    category: "surveillance",
    risk: "high",
    reason: "Identifies camera capture capability.",
    patterns: [/\bwebcam\b/i, /\bvideoCapture\b/i],
  },
  {
    id: "pay-command",
    term: "/pay",
    label: "/pay command",
    category: "command",
    risk: "medium",
    reason: "Hardcoded payment commands can automate in-game currency transfer.",
    patterns: [/(?:^|[\s"'`])\/pay(?:\s|$)/i],
  },
  {
    id: "login-command",
    term: "/login",
    label: "/login command",
    category: "command",
    risk: "high",
    reason: "Hardcoded login commands may collect or replay server passwords.",
    patterns: [/(?:^|[\s"'`])\/login(?:\s|$)/i],
  },
  {
    id: "register-command",
    term: "/register",
    label: "/register command",
    category: "command",
    risk: "medium",
    reason: "Hardcoded registration commands can expose server credentials.",
    patterns: [/(?:^|[\s"'`])\/register(?:\s|$)/i],
  },
  {
    id: "operator-command",
    term: "/op",
    label: "Operator command",
    category: "command",
    risk: "high",
    reason: "Hardcoded operator commands may attempt privilege escalation.",
    patterns: [/(?:^|[\s"'`])\/(?:op|deop)(?:\s|$)/i],
  },
  {
    id: "download",
    term: "download",
    label: "Download",
    category: "download",
    risk: "medium",
    reason: "Identifies remote content retrieval or a staged payload.",
    patterns: [
      /\bdownloads?\b/i,
      /\bopenStream\b/,
      /\bHttpClient\b/,
      /\bHttpURLConnection\b/,
      /\bFiles\.copy\b/,
      /\btransferFrom\b/,
    ],
  },
  {
    id: "payload",
    term: "payload",
    label: "Payload",
    category: "download",
    risk: "medium",
    reason: "Payload terminology can identify staged code or data.",
    patterns: [/\bpayload\b/i, /\bstage[123]\b/i, /\bdl\.jar\b/i],
  },
  {
    id: "dropper",
    term: "dropper",
    label: "Dropper",
    category: "download",
    risk: "high",
    reason: "Explicitly identifies code that installs a second-stage payload.",
    patterns: [/\bdropper\b/i, /\bdropped[_ .-]?payload\b/i],
  },
  {
    id: "webhook",
    term: "webhook",
    label: "Webhook",
    category: "exfiltration",
    risk: "medium",
    reason: "Webhooks are frequently used as simple exfiltration channels.",
    patterns: [/\bwebhooks?\b/i, /\/api\/webhooks\//i],
  },
  {
    id: "exfiltration",
    term: "exfil",
    label: "Exfiltration",
    category: "exfiltration",
    risk: "high",
    reason: "Explicitly identifies data leaving the host.",
    patterns: [/\bexfil(?:trate|tration)?\b/i, /\bsubmitMinecraftLog\b/i],
  },
  {
    id: "c2",
    term: "c2",
    label: "C2",
    category: "exfiltration",
    risk: "high",
    reason: "May identify command-and-control infrastructure.",
    patterns: [/\bC2\b/, /\bcommand[_ .-]?and[_ .-]?control\b/i],
  },
  {
    id: "persistence",
    term: "persistence",
    label: "Persistence",
    category: "persistence",
    risk: "high",
    reason: "Identifies attempts to survive reboot or login.",
    patterns: [
      /\bpersistence\b/i,
      /\bschtasks(?:\.exe)?\b/i,
      /\bautorun\b/i,
      /CurrentVersion[\\/]Run/i,
      /Start Menu[\\/]Programs[\\/]Startup/i,
    ],
  },
  {
    id: "defender-bypass",
    term: "defender",
    label: "Security tampering",
    category: "evasion",
    risk: "high",
    reason: "May identify antivirus exclusions or security-product tampering.",
    patterns: [/\bWindows Defender\b/i, /\bAdd-MpPreference\b/i, /\bexclusionPath\b/i],
  },
  {
    id: "anti-analysis",
    term: "anti-vm",
    label: "Anti-analysis",
    category: "evasion",
    risk: "medium",
    reason: "Identifies sandbox, VM, debugger, or analysis checks.",
    patterns: [
      /\banti[_ .-]?(?:vm|debug|sandbox)\b/i,
      /\bwireshark\b/i,
      /\bprocesshacker\b/i,
      /\bvirtualbox\b/i,
    ],
  },
  {
    id: "string-decryption",
    term: "decrypt",
    label: "String decryption",
    category: "obfuscation",
    risk: "context",
    reason: "Runtime decryption may conceal configuration or behavior.",
    patterns: [/\bdecrypt(?:ion|or)?\b/i, /\bxor\b/i, /\bBase64\b/, /\bCipher\.getInstance\b/],
  },
];

const riskOrder: Record<SuspiciousWordHit["risk"], number> = {
  high: 0,
  medium: 1,
  context: 2,
};

function testPattern(pattern: RegExp, value: string) {
  pattern.lastIndex = 0;
  return pattern.test(value);
}

export function matchSuspiciousWords(value: string): SuspiciousWordHit[] {
  const hits = SUSPICIOUS_WORD_DICTIONARY.filter((entry) =>
    entry.patterns.some((pattern) => testPattern(pattern, value)),
  ).map((entry) => ({
    id: entry.id,
    term: entry.term,
    label: entry.label,
    category: entry.category,
    risk: entry.risk,
    reason: entry.reason,
  }));

  const ids = new Set(hits.map((hit) => hit.id));
  return hits
    .filter((hit) => {
      if (hit.id === "token" && ids.has("access-token")) return false;
      if (hit.id === "token" && ids.has("refresh-token")) return false;
      if (hit.id === "token" && ids.has("session-token")) return false;
      if (hit.id === "uuid" && ids.has("minecraft-uuid")) return false;
      if (hit.id === "username" && ids.has("minecraft-username")) return false;
      return true;
    })
    .sort(
      (left, right) =>
        riskOrder[left.risk] - riskOrder[right.risk] ||
        left.label.localeCompare(right.label),
    );
}

export function summarizeSuspiciousWords(
  values: Array<{ value: string; entry: string; flags: SuspiciousWordHit[] }>,
): SuspiciousTermSummary[] {
  const summaries = new Map<
    string,
    SuspiciousWordHit & { count: number; entries: Set<string> }
  >();
  for (const value of values) {
    for (const hit of value.flags) {
      const current = summaries.get(hit.id) ?? {
        ...hit,
        count: 0,
        entries: new Set<string>(),
      };
      current.count += 1;
      current.entries.add(value.entry);
      summaries.set(hit.id, current);
    }
  }
  return Array.from(summaries.values())
    .map(({ entries, ...summary }) => ({
      ...summary,
      entries: entries.size,
    }))
    .sort(
      (left, right) =>
        riskOrder[left.risk] - riskOrder[right.risk] ||
        right.count - left.count ||
        left.label.localeCompare(right.label),
    );
}
